import {
  RAW_MATERIAL_KIND_LABELS,
  type CreateOpeningStockLineReq,
  type OpeningStockLine,
  type RawMaterialKind,
} from "@artisancode/api-types";

export interface FormLine {
  itemType: "raw_material" | "product";
  itemId: string;
  itemName: string;
  itemSku: string | null;
  kind?: RawMaterialKind | null;
  sourcingType?: "produced" | "purchased" | null;
  uomName: string;
  qtyOnHand: number;
  qtyRepacking: number;
  unitCost: number;
  subtotalCost: number;
}

/** What the page needs to know about an item beyond the line itself. */
export interface ItemInfo {
  ingredientId?: string | null;
  packQty?: number | null;
  cogs?: number | null;
}

export const isIngredient = (l: FormLine) =>
  l.itemType === "raw_material" && l.kind === "ingredient";
export const isProduced = (l: FormLine) =>
  l.itemType === "product" && l.sourcingType === "produced";
/** Warehouse column applies to everything except ingredients (bar only). */
export const hasWarehouseQty = (l: FormLine) => !isIngredient(l);
/** Bar column applies to raw materials that are not purchased packs. */
export const hasBarQty = (l: FormLine) =>
  l.itemType === "raw_material" && l.kind !== "purchased";
/** Cost is typed in unless the system derives it (ingredient, produced product). */
export const hasEnteredCost = (l: FormLine) =>
  !isIngredient(l) && !isProduced(l);

export function itemTypeLabel(l: FormLine): string {
  if (l.itemType === "product") {
    return l.sourcingType === "produced" ? "Produk Racikan" : "Produk Beli";
  }
  return l.kind ? RAW_MATERIAL_KIND_LABELS[l.kind] : "Bahan";
}

/**
 * Mirrors the server rules for display: ingredient cost = linked purchased line's cost ÷ packQty
 * (0 until that line is in the document), produced product cost = recipe COGS.
 */
export function computeLines(
  lines: FormLine[],
  info: (itemId: string) => ItemInfo | undefined,
): FormLine[] {
  return lines.map((line) => {
    let unitCost = line.unitCost;
    if (isIngredient(line)) {
      const pack = lines.find(
        (l) =>
          l.kind === "purchased" &&
          info(l.itemId)?.ingredientId === line.itemId,
      );
      const packQty = pack ? Number(info(pack.itemId)?.packQty) || 0 : 0;
      unitCost = pack && packQty > 0 ? pack.unitCost / packQty : 0;
    } else if (isProduced(line)) {
      unitCost = Number(info(line.itemId)?.cogs) || 0;
    }
    const qty =
      (hasWarehouseQty(line) ? line.qtyOnHand : 0) +
      (hasBarQty(line) ? line.qtyRepacking : 0);
    return { ...line, unitCost, subtotalCost: Math.round(qty * unitCost) };
  });
}

export function fromDocLine(l: OpeningStockLine): FormLine {
  return {
    itemType: l.itemType,
    itemId: l.itemId,
    itemName: l.itemName,
    itemSku: l.itemSku,
    kind: l.kind,
    sourcingType: l.sourcingType,
    uomName: l.uomName,
    qtyOnHand: l.qtyOnHand,
    qtyRepacking: l.qtyRepacking,
    unitCost: l.unitCost,
    subtotalCost: l.subtotalCost,
  };
}

export function toPayloadLines(lines: FormLine[]): CreateOpeningStockLineReq[] {
  return lines.map((l) => ({
    itemType: l.itemType,
    itemId: l.itemId,
    qtyOnHand: l.qtyOnHand,
    qtyRepacking: l.qtyRepacking,
    unitCost: l.unitCost,
  }));
}

export function formatDate(value: string | Date | null | undefined): string {
  if (!value) return "—";
  return new Date(value).toLocaleDateString("id-ID", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}
